import { IMAGE_ASSET_TYPES, TEXT_ASSET_TYPES } from '../constants/googleAssetTypes';

export const ASPECT_RATIO_TOLERANCE = 0.01;

const formatRatio = (ratio) => `${ratio.toFixed(2)}:1`;

const findFieldTypeConfig = (fieldType) => IMAGE_ASSET_TYPES.find((t) => t.value === fieldType);

const ratiosMatch = (actual, expected, tolerance = ASPECT_RATIO_TOLERANCE) =>
  Math.abs(actual - expected) <= expected * tolerance;

export const validateAssetAspectRatio = (asset, fieldType) => {
  if (!asset || asset.type !== 'IMAGE') {
    return { valid: true };
  }

  const config = findFieldTypeConfig(fieldType);
  if (!config || !config.aspectRatio) {
    return { valid: true };
  }

  const { width, height } = asset;
  if (!width || !height) {
    console.warn('[googleAssetValidation] missing dimensions for asset', asset?.resourceName);
    return { valid: true };
  }

  const actualRatio = width / height;
  const ratioOk = ratiosMatch(actualRatio, config.aspectRatio);
  const tooSmall =
    (config.minWidth && width < config.minWidth) || (config.minHeight && height < config.minHeight);

  let suggestedFieldType = null;
  if (!ratioOk) {
    // Wrong aspect ratio: skip same-ratio types since the switch wouldn't change validation outcome.
    const match = IMAGE_ASSET_TYPES.find(
      (t) =>
        t.value !== fieldType &&
        t.aspectRatio !== config.aspectRatio &&
        ratiosMatch(actualRatio, t.aspectRatio)
    );
    suggestedFieldType = match?.value || null;
  } else if (tooSmall) {
    // Right ratio but too small: same-ratio types with a lower minimum are valid suggestions.
    const match = IMAGE_ASSET_TYPES.find(
      (t) =>
        t.value !== fieldType &&
        ratiosMatch(actualRatio, t.aspectRatio) &&
        (!t.minWidth || width >= t.minWidth) &&
        (!t.minHeight || height >= t.minHeight)
    );
    suggestedFieldType = match?.value || null;
  }

  return {
    valid: ratioOk && !tooSmall,
    wrongRatio: !ratioOk,
    tooSmall,
    actualRatio,
    actualRatioLabel: formatRatio(actualRatio),
    expectedRatio: config.aspectRatio,
    expectedLabel: config.aspectRatioLabel,
    minWidth: config.minWidth,
    minHeight: config.minHeight,
    recommended: config.recommendedDimensions,
    suggestedFieldType,
  };
};

export const withImageSpecLabels = (types = IMAGE_ASSET_TYPES) =>
  types.map((t) => ({
    value: t.value,
    label: t.aspectRatioLabel
      ? `${t.label} — ${t.aspectRatioLabel} (${t.recommendedDimensions})`
      : t.label,
  }));

export const withTextLengthLabels = (types = TEXT_ASSET_TYPES) =>
  types.map((t) => ({
    value: t.value,
    label: t.maxLength ? `${t.label} (max ${t.maxLength} chars)` : t.label,
  }));

export const validateAssetTextLength = (asset, fieldType) => {
  if (!asset || asset.type !== 'TEXT') {
    return { valid: true };
  }
  const config = TEXT_ASSET_TYPES.find((t) => t.value === fieldType);
  if (!config) {
    return { valid: true };
  }
  const text = asset.text || '';
  const length = text.length;
  const valid = length <= config.maxLength;

  let suggestedFieldType = null;
  if (!valid) {
    const match = TEXT_ASSET_TYPES.find((t) => t.value !== fieldType && length <= t.maxLength);
    suggestedFieldType = match?.value || null;
  }

  return {
    valid,
    length,
    maxLength: config.maxLength,
    suggestedFieldType,
  };
};

